/**
 * ModernSidebar.tsx
 * Barre latérale gauche moderne style Figma/Canva
 * Icônes uniquement (60px) avec système de Drawer
 */
import React, { useState, useCallback } from 'react';
import { ProjectDrawer } from './Drawers/ProjectDrawer';
import { TemplatesDrawer } from './Drawers/TemplatesDrawer';
import { LayoutsDrawer } from './Drawers/LayoutsDrawer';
import { TextDrawer } from './Drawers/TextDrawer';
import { ElementsDrawer } from './Drawers/ElementsDrawer';
import { UploadsDrawer } from './Drawers/UploadsDrawer';
import { logger } from '../../../utils/ProfessionalLogger';
import { IconButton } from '../../../ui';
import { FileText, LayoutGrid, Columns, Type, Sparkles, Upload, Menu, X } from 'lucide-react';

type DrawerType = 'project' | 'templates' | 'layouts' | 'text' | 'elements' | 'uploads' | null;

/**
 * Icônes Lucide pour la sidebar
 */
const Icons = {
  project: <FileText className="w-5 h-5" />,
  templates: <LayoutGrid className="w-5 h-5" />,
  layouts: <Columns className="w-5 h-5" />,
  text: <Type className="w-5 h-5" />,
  elements: <Sparkles className="w-5 h-5" />,
  uploads: <Upload className="w-5 h-5" />,
};

/**
 * Props pour ModernSidebar
 */
interface ModernSidebarProps {
  onDrawerToggle?: (open: boolean) => void;
  drawerOpen?: boolean;
  onDrawerChange?: (drawerType: DrawerType) => void;
  activeDrawer?: DrawerType; // État externe du drawer actif
}

/**
 * Composant ModernSidebar
 */
export function ModernSidebar({
  onDrawerToggle,
  drawerOpen = true,
  onDrawerChange,
  activeDrawer: externalActiveDrawer,
}: ModernSidebarProps): React.ReactElement {
  // Si onDrawerChange est fourni, utiliser l'état externe, sinon utiliser l'état interne
  const [internalDrawer, setInternalDrawer] = useState<DrawerType>(null);
  const activeDrawer = externalActiveDrawer !== undefined ? externalActiveDrawer : internalDrawer;

  const handleIconClick = useCallback(
    (drawerType: DrawerType) => {
      logger.debug('[ModernSidebar] handleIconClick', { drawerType, activeDrawer });
      // Toggle: si le drawer est déjà ouvert, on le ferme
      const newActiveDrawer = activeDrawer === drawerType ? null : drawerType;
      logger.debug('[ModernSidebar] newActiveDrawer', { newActiveDrawer });

      if (onDrawerChange) {
        logger.debug('[ModernSidebar] Utilisation de onDrawerChange (état externe)');
        // Utiliser l'état externe - onDrawerChange gère déjà l'ouverture/fermeture
        onDrawerChange(newActiveDrawer);
        // onDrawerToggle est appelé uniquement pour notifier l'état (optionnel)
        if (onDrawerToggle) {
          onDrawerToggle(newActiveDrawer !== null);
        }
      } else {
        logger.debug('[ModernSidebar] Utilisation de setInternalDrawer (état interne)');
        // Utiliser l'état interne
        setInternalDrawer(newActiveDrawer);
        if (onDrawerToggle) {
          onDrawerToggle(newActiveDrawer !== null);
        }
      }
    },
    [activeDrawer, onDrawerToggle, onDrawerChange]
  );

  const sidebarItems = [
    {
      id: 'project' as DrawerType,
      icon: Icons.project,
      label: 'Projet',
      tooltip: 'Paramètres du projet',
    },
    {
      id: 'templates' as DrawerType,
      icon: Icons.templates,
      label: 'Templates',
      tooltip: 'Modèles prédéfinis',
    },
    {
      id: 'layouts' as DrawerType,
      icon: Icons.layouts,
      label: 'Mise en page',
      tooltip: 'Layouts photobooth',
    },
    { id: 'text' as DrawerType, icon: Icons.text, label: 'Texte', tooltip: 'Ajouter du texte' },
    {
      id: 'elements' as DrawerType,
      icon: Icons.elements,
      label: 'Éléments',
      tooltip: 'Formes et stickers',
    },
    { id: 'uploads' as DrawerType, icon: Icons.uploads, label: 'Uploads', tooltip: 'Mes images' },
  ];

  // Détection mobile pour responsive
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // CORRIGÉ: État pour savoir si la sidebar est ouverte sur mobile
  const isSidebarOpen = activeDrawer !== null && drawerOpen;

  // CORRIGÉ: Fonction pour toggle la sidebar sur mobile
  const toggleSidebar = useCallback(() => {
    if (isSidebarOpen) {
      // Fermer la sidebar
      if (onDrawerChange) {
        onDrawerChange(null);
      } else {
        setInternalDrawer(null);
      }
      if (onDrawerToggle) {
        onDrawerToggle(false);
      }
    } else {
      // Ouvrir la sidebar avec le premier item
      const firstDrawer: DrawerType = 'project';
      if (onDrawerChange) {
        onDrawerChange(firstDrawer);
      } else {
        setInternalDrawer(firstDrawer);
      }
      if (onDrawerToggle) {
        onDrawerToggle(true);
      }
    }
  }, [isSidebarOpen, onDrawerChange, onDrawerToggle]);

  // Déterminer si la sidebar doit être collapsed sur mobile
  const shouldCollapse = isMobile && (activeDrawer === null || !drawerOpen);

  return (
    <>
      {/* PHASE 3: Bouton hamburger pour mobile */}
      {isMobile && (
        <IconButton
          variant="primary"
          size="lg"
          onClick={toggleSidebar}
          aria-label={isSidebarOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
          icon={isSidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          className="fixed top-4 left-4 z-[1001] shadow-lg"
        />
      )}

      {/* PHASE 3: Overlay pour fermer la sidebar sur mobile */}
      {isMobile && isSidebarOpen && (
        <div
          className="mobile-sidebar-overlay"
          onClick={toggleSidebar}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 999,
          }}
        />
      )}

      <aside
        id="leftSidebar"
        className={`snapbooth-slim-sidebar ${shouldCollapse ? 'collapsed' : ''}`}
        role="complementary"
        aria-label="Barre d'outils principale"
        style={{
          ...(isMobile && {
            zIndex: 1000,
            boxShadow: isSidebarOpen ? '2px 0 8px rgba(0, 0, 0, 0.2)' : 'none',
          }),
        }}
      >
        {sidebarItems.map(item => {
          const isActive = activeDrawer === item.id;
          return (
            <IconButton
              key={item.id}
              variant="ghost"
              size="lg"
              onClick={() => handleIconClick(item.id)}
              title={item.tooltip}
              aria-label={item.label}
              aria-pressed={isActive}
              aria-describedby={`tooltip-${item.id}`}
              aria-expanded={isActive && activeDrawer === item.id}
              aria-controls={isActive && activeDrawer === item.id ? `drawer-${item.id}` : undefined}
              icon={item.icon}
              className={`slim-sidebar-icon ${isActive ? 'active bg-blue-50 text-blue-600' : ''}`}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleIconClick(item.id);
                }
              }}
            >
              <span id={`tooltip-${item.id}`} className="sr-only">
                {item.tooltip}
              </span>
            </IconButton>
          );
        })}
      </aside>
    </>
  );
}

/**
 * Contenu du Drawer selon le type
 */
export const DrawerContent: React.FC<{ type: DrawerType; onClose: () => void }> = ({
  type,
  onClose,
}) => {
  logger.debug('[DrawerContent] Rendu', { type });
  switch (type) {
    case 'project':
      logger.debug('[DrawerContent] Rendu ProjectDrawer');
      return <ProjectDrawer onClose={onClose} />;
    case 'templates':
      logger.debug('[DrawerContent] Rendu TemplatesDrawer');
      return <TemplatesDrawer onClose={onClose} />;
    case 'layouts':
      logger.debug('[DrawerContent] Rendu LayoutsDrawer');
      return <LayoutsDrawer onClose={onClose} />;
    case 'text':
      logger.debug('[DrawerContent] Rendu TextDrawer');
      return <TextDrawer onClose={onClose} />;
    case 'elements':
      logger.debug('[DrawerContent] Rendu ElementsDrawer');
      return <ElementsDrawer onClose={onClose} />;
    case 'uploads':
      logger.debug('[DrawerContent] Rendu UploadsDrawer');
      return <UploadsDrawer onClose={onClose} />;
    default:
      logger.warn('[DrawerContent] Type non reconnu', { type });
      return null;
  }
};

// ProjectDrawer est maintenant dans Drawers/ProjectDrawer.tsx
// La définition locale a été supprimée pour éviter les duplications

// TemplatesDrawer est maintenant dans Drawers/TemplatesDrawer.tsx
// LayoutsDrawer est maintenant dans Drawers/LayoutsDrawer.tsx
// TextDrawer, ElementsDrawer et UploadsDrawer sont maintenant dans Drawers/
// Les fragments orphelins ont été supprimés car dupliqués dans les fichiers Drawers/
